import { types as api } from '@mesa-labs/mesa-api';
import {
  useCallback, useMemo, useRef, useState,
} from 'react';

import {
  useCreateVendorActionItemMutation,
  useCreateVendorActionItemNoteMutation,
  useGetAllVendorActionItemsQuery,
  useGetVendorActionItemAssigneesQuery,
  useGetVendorActionItemsQuery,
  useGetVendorQuery,
  useUpdateVendorActionItemMutation,
} from '../../../redux/api/vendors';
import { useSelector } from '../../../redux/hooks';
import { AuthenticationGroup } from '../../../redux/slices/auth';
import { getErrorMessage } from '../../../utils';
import {
  byLastActivityDesc, canWriteActionItems, isOpenActionItem, MyOpenActionItemsParams,
} from '../../../utils/vendorActionItems';

const NoItems: readonly api.VendorActionItemResponse[] = [];

export const useMeEmail = (): string | undefined => useSelector((state) => state.auth.username) as string | undefined;

export const useCanWriteActionItems = (): boolean => {
  const roles = useSelector((state) => state.auth.roles);
  const isReadOnly = useSelector((state) => state.auth.isReadOnly);
  return canWriteActionItems({ isReadOnly, isVendorManagement: roles.includes(AuthenticationGroup.VENDOR_MANAGEMENT) });
};

// The viewer's open Action Items for the nav menu, or undefined for non-operators, who can't read them. Items may briefly
// include one just closed: a status change patches the cached list before the refetch drops it.
export const useMyOpenActionItems = (): readonly api.VendorActionItemResponse[] | undefined => {
  const isOperator = useSelector((state) => state.auth.isLoggedIn && state.auth.roles.includes(AuthenticationGroup.OPERATORS));
  const { data = NoItems } = useGetAllVendorActionItemsQuery(MyOpenActionItemsParams, { skip: !isOperator });
  return isOperator ? data : undefined;
};

const NoOperators: readonly api.VendorActionItemAssigneeResponse[] = [];

// Operators for assignee pickers, in the server's last-then-first-name order; `skip` defers the lookup until a picker
// can be shown.
export const useOperators = (skip = false): readonly api.VendorActionItemAssigneeResponse[] => {
  const { data } = useGetVendorActionItemAssigneesQuery(undefined, { skip });
  return data ?? NoOperators;
};

export type WriteAttempt = {
  readonly error?: string;
  readonly clearError: () => void;
  // Resolves to the result, or undefined after recording the failure as `error`.
  readonly attempt: <T>(run: () => Promise<T>, fallback: string) => Promise<T | undefined>;
};

// Runs writes and keeps the latest failure as a single dismissible message.
export const useWriteAttempt = (): WriteAttempt => {
  const [error, setError] = useState<string | undefined>();

  const attempt = useCallback(async <T,>(run: () => Promise<T>, fallback: string): Promise<T | undefined> => {
    setError(undefined);
    try {
      return await run();
    } catch (e) {
      setError(getErrorMessage(e) || fallback);
      return undefined;
    }
  }, []);

  return { error, clearError: useCallback(() => setError(undefined), []), attempt };
};

export type ActionItemActions = {
  readonly error?: string;
  readonly clearError: () => void;
  readonly create: (request: api.CreateVendorActionItemRequest) => Promise<api.VendorActionItemResponse | undefined>;
  // Each resolves to whether the write succeeded.
  readonly update: (vendorActionItemId: string, request: api.UpdateVendorActionItemRequest) => Promise<boolean>;
  readonly addNote: (vendorActionItemId: string, note: string) => Promise<boolean>;
};

// Write operations for any Vendor's Action Items (the board spans Vendors); failures surface as a single dismissible
// message shared by every write.
export type ActionItemWrites = {
  readonly error?: string;
  readonly clearError: () => void;
  readonly create: (vendorId: string, request: api.CreateVendorActionItemRequest) => Promise<api.VendorActionItemResponse | undefined>;
  // Each resolves to whether the write succeeded.
  readonly update: (vendorId: string, vendorActionItemId: string, request: api.UpdateVendorActionItemRequest) => Promise<boolean>;
  readonly addNote: (vendorId: string, vendorActionItemId: string, note: string) => Promise<boolean>;
};

export const useActionItemWrites = (): ActionItemWrites => {
  const { error, clearError, attempt } = useWriteAttempt();
  const [createItem] = useCreateVendorActionItemMutation();
  const [updateItem] = useUpdateVendorActionItemMutation();
  const [createItemNote] = useCreateVendorActionItemNoteMutation();

  return {
    error,
    clearError,
    create: (vendorId, request) => attempt(() => createItem({ vendorId, ...request }).unwrap(), 'There was an error creating the Action Item'),
    update: async (vendorId, vendorActionItemId, request) => (
      await attempt(() => updateItem({ vendorId, vendorActionItemId, ...request }).unwrap(), 'There was an error updating the Action Item')
    ) !== undefined,
    addNote: async (vendorId, vendorActionItemId, note) => (
      await attempt(() => createItemNote({ vendorId, vendorActionItemId, note }).unwrap(), 'There was an error adding the comment')
    ) !== undefined,
  };
};

// Binds writes to one Vendor, for the components that work on a single Vendor's items.
export const bindActionItemActions = (writes: ActionItemWrites, vendorId: string): ActionItemActions => ({
  error: writes.error,
  clearError: writes.clearError,
  create: (request) => writes.create(vendorId, request),
  update: (vendorActionItemId, request) => writes.update(vendorId, vendorActionItemId, request),
  addNote: (vendorActionItemId, note) => writes.addNote(vendorId, vendorActionItemId, note),
});

// Write operations for one Vendor's Action Items; failures surface as a single dismissible message.
export const useActionItemActions = (vendorId: string): ActionItemActions => bindActionItemActions(useActionItemWrites(), vendorId);

// What the right-hand drawer shows. Detail and create share one drawer, so opening either closes the other.
export type ActionItemPanel =
  | { readonly kind: 'none' }
  | { readonly kind: 'detail'; readonly id: string }
  | { readonly kind: 'create'; readonly sourceNote?: api.VendorNoteResponse };

const NoPanel: ActionItemPanel = { kind: 'none' };

export type VendorActionItemsPanel = {
  readonly openItems: readonly api.VendorActionItemResponse[];
  readonly closedItems: readonly api.VendorActionItemResponse[];
  readonly items: readonly api.VendorActionItemResponse[];
  readonly isLoading: boolean;
  readonly actions: ActionItemActions;
  readonly panel: ActionItemPanel;
  readonly selected?: api.VendorActionItemResponse;
  readonly toggleDetail: (id: string) => void;
  readonly showDetail: (id: string) => void;
  // Blank create, or create from a Note.
  readonly startCreate: (sourceNote?: api.VendorNoteResponse) => void;
  readonly close: () => void;
};

// One Vendor's Action Items (newest activity first) plus the detail/create drawer state shared by every surface.
export const useVendorActionItemsPanel = (vendorId: string, { skip = false } = {}): VendorActionItemsPanel => {
  const actions = useActionItemActions(vendorId);
  const [panel, setPanel] = useState<ActionItemPanel>(NoPanel);
  const { data: items = NoItems, isLoading } = useGetVendorActionItemsQuery({ vendorId }, { skip });

  const openItems = useMemo(() => items.filter(isOpenActionItem).sort(byLastActivityDesc), [items]);
  const closedItems = useMemo(() => items.filter((item) => !isOpenActionItem(item)).sort(byLastActivityDesc), [items]);
  const selected = panel.kind === 'detail' ? items.find((item) => item.id === panel.id) : undefined;
  const { clearError } = actions;

  return {
    items,
    isLoading,
    openItems,
    closedItems,
    actions,
    panel,
    selected,
    toggleDetail: (id) => setPanel((current) => (current.kind === 'detail' && current.id === id ? NoPanel : { kind: 'detail', id })),
    showDetail: (id) => setPanel({ kind: 'detail', id }),
    startCreate: (sourceNote) => { clearError(); setPanel({ kind: 'create', sourceNote }); },
    close: () => { clearError(); setPanel(NoPanel); },
  };
};

// The board's drawer: one item's detail (any Vendor) or the create form.
export type BoardPanel =
  | { readonly kind: 'none' }
  | { readonly kind: 'detail'; readonly id: string; readonly vendorId: string }
  | { readonly kind: 'create' };

const NoBoardPanel: BoardPanel = { kind: 'none' };

type ItemRef = Pick<api.VendorActionItemResponse, 'id' | 'vendorId'>;

export type ActionItemsBoardPanel = {
  readonly writes: ActionItemWrites;
  readonly panel: BoardPanel;
  readonly selected?: api.VendorActionItemResponse;
  readonly selectedVendorName?: string;
  // `writes` bound to the selected item's Vendor, for the detail drawer.
  readonly selectedActions: ActionItemActions;
  readonly toggleDetail: (item: ItemRef) => void;
  readonly showDetail: (item: ItemRef) => void;
  readonly startCreate: () => void;
  readonly close: () => void;
};

type Selection = { readonly item: api.VendorActionItemResponse; readonly vendorName?: string };

// Drawer state for the cross-Vendor board. The open item is read from the board's list; when the filters hide it
// (created for someone else, or just canceled) it is read from its Vendor's list so the drawer stays open.
export const useActionItemsBoardPanel = (items: readonly api.VendorActionItemResponse[]): ActionItemsBoardPanel => {
  const writes = useActionItemWrites();
  const [panel, setPanel] = useState<BoardPanel>(NoBoardPanel);
  const detail = panel.kind === 'detail' ? panel : undefined;

  const onBoard = detail && items.find((item) => item.id === detail.id);
  const { data: vendorItems } = useGetVendorActionItemsQuery({ vendorId: detail?.vendorId ?? '' }, { skip: !detail || !!onBoard });
  const { data: vendor } = useGetVendorQuery({ id: detail?.vendorId ?? '' }, { skip: !detail || !!onBoard?.vendorName });

  // The last resolved selection bridges the moment between the item leaving the board and its Vendor's list loading.
  const last = useRef<Selection | undefined>(undefined);
  const resolved = onBoard ?? vendorItems?.find((item) => item.id === detail?.id);
  const selection: Selection | undefined = resolved
    ? { item: resolved, vendorName: onBoard?.vendorName ?? vendor?.name ?? last.current?.vendorName }
    : (detail && last.current?.item.id === detail.id && last.current) || undefined;
  last.current = selection;

  const { clearError } = writes;
  return {
    writes,
    panel,
    selected: selection?.item,
    selectedVendorName: selection?.vendorName,
    selectedActions: bindActionItemActions(writes, detail?.vendorId ?? ''),
    toggleDetail: ({ id, vendorId }) => setPanel((current) => (
      current.kind === 'detail' && current.id === id ? NoBoardPanel : { kind: 'detail', id, vendorId }
    )),
    showDetail: ({ id, vendorId }) => setPanel({ kind: 'detail', id, vendorId }),
    startCreate: () => { clearError(); setPanel({ kind: 'create' }); },
    close: () => { clearError(); setPanel(NoBoardPanel); },
  };
};

export type ActionItemDraft = {
  readonly title: string;
  readonly setTitle: (title: string) => void;
  readonly description: string;
  readonly setDescription: (description: string) => void;
  readonly assignedTo: string;
  readonly setAssignedTo: (assignedTo: string) => void;
  readonly status: api.VendorActionItemStatus;
  readonly setStatus: (status: api.VendorActionItemStatus) => void;
  readonly saving: boolean;
  // A title and an assignee are required.
  readonly isValid: boolean;
  readonly submit: () => Promise<void>;
};

type DraftOptions = {
  readonly initialAssignee?: string;
  // Create-from-Note: recorded as the item's source; nothing is prefilled.
  readonly sourceNote?: api.VendorNoteResponse;
  readonly onCreate: (request: api.CreateVendorActionItemRequest) => Promise<unknown>;
};

// Form state for creating an Action Item; the create forms differ only in layout.
export const useActionItemDraft = ({ initialAssignee = '', sourceNote, onCreate }: DraftOptions): ActionItemDraft => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState(initialAssignee);
  const [status, setStatus] = useState(api.VendorActionItemStatus.TODO);
  const [saving, setSaving] = useState(false);
  const isValid = !!title.trim() && !!assignedTo;

  const submit = async () => {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        assignedTo,
        status,
        sourceNoteId: sourceNote?.id,
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    title, setTitle, description, setDescription, assignedTo, setAssignedTo, status, setStatus, saving, isValid, submit,
  };
};
